import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useGetMigrationLazy } from '@src/adapters/hooks/migration/useGetMigration';
import Layout from '@src/components/layout/Layout';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Information from './components/Information';
import PDFPreviewSection from './components/PDFPreviewSection';
import MigrationDetailDTO from '@src/adapters/dto/migration/MigrationDetailDTO';
import MigrationNoteDTO from '@src/adapters/dto/migration/MigrationNoteDTO';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import { useGetMigrationItemsLazy } from '@src/adapters/hooks/migration/useGetMigrationItems';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import { MigrationItemSuggestionStatus } from '@src/constants/MigrationItemSuggestionStatus';
import useSaveMigrationSuggestionLazy from '@src/adapters/hooks/migration/useSaveMigrationSuggestion';
import { useDeleteMigrationItemsLazy } from '@src/adapters/hooks/migration/useDeleteMigrationItem';
import { useCreateMigrationItemsFromSuggestionsLazy } from '@src/adapters/hooks/migration/useCreateMigrationItemAutomatically';
import InventoryDialog from './components/InventoryDialog';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';
import PreviewDialog from './components/PreviewDialog';
import ConfirmationDialog from '@src/components/utilities/dialogs/ConfirmationDialog';
import {
  MigrationStatus,
  MigrationStatusesBeingMigrated,
} from '@src/constants/MigrationStatus';
import MigrationStatusSelection from '@src/components/migration/MigrationStatusSelection';
import { useSaveMigrationStatusLazy } from '@src/adapters/hooks/migration/useSaveMigrationStatus';
import MigrationSuggestionDTO from '@src/adapters/dto/migration/MigrationSuggestionDTO';
import useSaveMigrationItemApprovalLazy from '@src/adapters/hooks/migration/useSaveMigrationItemApproval';
import CloseIcon from '@mui/icons-material/Close';
import MigrationItemApprovalDTO from '@src/adapters/dto/migration/MigrationItemApprovalDTO';
import useAuthStore from '@src/auth/stores/useAuthStore';

function MigrationDetails() {
  const location = useLocation();
  const { loggedInUser } = useAuthStore();
  const { execute: getMigration } = useGetMigrationLazy();
  const { execute: getItems } = useGetMigrationItemsLazy();
  const { execute: saveMigration } = useSaveMigrationSuggestionLazy();
  const { execute: deleteItem } = useDeleteMigrationItemsLazy();
  const { execute: createAuto } = useCreateMigrationItemsFromSuggestionsLazy();
  const [migration, setMigration] = useState<MigrationDetailDTO | undefined>(
    undefined
  );
  const [statusOpen, setStatusOpen] = useState(false);
  const [items, setItems] = useState<MigrationItemViewDTO[]>([]);
  const statusRef = useRef(null);
  const [highlightItemId, setHighlightItemId] = useState<string>('');
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { execute: saveMigrationStatus } = useSaveMigrationStatusLazy();
  const { execute: saveItemApproval } = useSaveMigrationItemApprovalLazy();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const search = async () => {
      const queryParams = new URLSearchParams(location.search);
      const id = queryParams.get('id');
      if (id && isMounted) {
        getMigration({ id }).then((result) => {
          getItems({ id }).then((result) => {
            setItems(result.items);
          });
          setMigration(result.migration);
        });
      }
    };
    search();
    return () => {
      isMounted = false;
    };
  }, [getItems, getMigration, location.search]);

  const handleNoteUpdated = (note: MigrationNoteDTO) => {
    if (migration) {
      let updatedMigration = { ...migration };
      let updatedNotes = updatedMigration?.notes.map((n) =>
        n.id === note.id ? note : n
      );
      if (updatedNotes === undefined) {
        updatedNotes = [];
      }

      if (!updatedNotes.some((n) => n.id === note.id)) {
        updatedNotes.push(note);
      }

      updatedMigration.notes = updatedNotes;

      setMigration(updatedMigration);
    }
  };

  const handleNoteDeleted = (noteId: string) => {
    if (migration) {
      let updatedMigration = { ...migration };
      updatedMigration.notes = updatedMigration?.notes.filter(
        (n) => n.id !== noteId
      );

      setMigration(updatedMigration);
    }
  };

  const handleProcedureUpdated = (procedure: IdNameDto) => {
    if (migration) {
      const updatedMigration = migration;
      updatedMigration.procedure = procedure;
      setMigration(updatedMigration);
    }
  };

  const handleStatus = () => {
    setStatusOpen((prev) => !prev);
  };

  const handleStatusClosed = (status: MigrationStatus) => {
    if (migration) {
      const current = { ...migration };
      current.status = status;
      setMigration(current);
    }

    setStatusOpen(false);
  };

  const handleHighlightItem = (suggestionId: string) => {
    setHighlightItemId(suggestionId);
  };

  const setSuggestionStatus = (suggestionId: string, status: string) => {
    if (migration) {
      let currentSuggestions = migration?.suggestions;
      currentSuggestions = migration?.suggestions.map((prev) =>
        prev.id === suggestionId
          ? { ...prev, status: status as MigrationItemSuggestionStatus }
          : prev
      );

      migration.suggestions = currentSuggestions;
    }
  };

  const handleSuggestionUpdate = async (
    suggestion: MigrationSuggestionDTO,
    status: MigrationItemSuggestionStatus,
    inventoryId?: string,
    qtyOpen?: number,
    qtyOnHand?: number,
    note?: string,
    alias?: boolean,
    isLoan?: boolean
  ) => {
    if (migration) {
      const result = await saveMigration({
        migrationId: migration.id,
        suggestionId: suggestion.id,
        status: status,
        inventoryId,
        qtyOpen,
        qtyOnHand,
        note,
        alias,
        isLoan,
      });

      setSuggestionStatus(suggestion.id, status);
      setHighlightItemId('');
      if (result.migrationItem) {
        setItems((prev) => [
          ...prev,
          result.migrationItem as MigrationItemViewDTO,
        ]);
      }
    }
  };

  const handleDeleteItem = (id: string) => {
    deleteItem({ id: id }).then(() => {
      setItems((prevItems) => {
        const itemDeleted = prevItems.find((i) => i.id === id);
        if (itemDeleted && itemDeleted.suggestion) {
          setSuggestionStatus(
            itemDeleted.suggestion.id,
            MigrationItemSuggestionStatus.Unchecked
          );
        }

        prevItems = prevItems.filter((i) => i.id !== id);

        return prevItems;
      });
    });
  };

  const handleItemUpdated = (item: MigrationItemUpdateDTO) => {
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id
          ? {
              ...i,
              note: item.note,
              isLoan: item.isLoan,
              qtyOnHand: item.onHand,
              qtyOpen: item.open,
            }
          : i
      )
    );
  };

  const handleRefreshSuggestions = () => {
    if (migration) {
      setLoading(true);
      createAuto({ migrationId: migration.id }).then((result) => {
        if (result) {
          getMigration({ id: migration.id }).then((result) => {
            getItems({ id: migration.id }).then((result) => {
              setItems(result.items);
              setLoading(false);
            });
            setMigration(result.migration);
          });
        }
      });
    }
  };

  const handleInventoryItemAdded = (item: MigrationItemViewDTO) => {
    setItems((prev) => [...prev, item]);
  };

  const handleGlobalInventory = () => {
    setInventoryOpen(true);
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleSendForApproval = () => {
    setConfirmOpen(true);
  };

  const handleConfirmClicked = async (button: string) => {
    if (button.toLowerCase() === 'cancel') {
      setConfirmOpen(false);
    } else {
      if (migration) {
        await saveMigrationStatus({
          id: migration.id,
          status: MigrationStatus.InApproval,
        });
        navigate('/Migration');
      }
      setConfirmOpen(false);
    }
  };

  const handleItemApprovalUpdate = async (
    itemId: string,
    suggestionId: string,
    approved: boolean
  ) => {
    if (migration) {
      //not waiting for the update to finish to refresh view and tick in check box
      //as it takes to long so recreating what would have been returned if we waited

      saveItemApproval({
        id: itemId,
        approved,
      });

      if (suggestionId) {
        setSuggestionStatus(
          suggestionId,
          approved
            ? MigrationItemSuggestionStatus.Checked
            : MigrationItemSuggestionStatus.Matched
        );
      }

      let approval: MigrationItemApprovalDTO | undefined;

      if (approved) {
        approval = {
          userId: loggedInUser.id,
          date: new Date(),
        };
      }

      setItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, approval } : i))
      );
    }
  };

  const handleApprove = async () => {
    if (migration) {
      await saveMigrationStatus({
        id: migration.id,
        status: MigrationStatus.Complete,
      });

      const currentMigration = { ...migration };
      currentMigration.status = MigrationStatus.Complete;
      setMigration(currentMigration);
    }
  };

  const handleClose = () => {
    navigate('/Migration');
  };

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {migration && (
        <Layout>
          <Box
            sx={{
              padding: '0 20px 20px 20px',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <Box>
              <Typography variant="h4">{migration.originalFilename}</Typography>
            </Box>
            <Box sx={(theme) => ({ display: 'flex', gap: theme.spacing(2) })}>
              {MigrationStatusesBeingMigrated.includes(migration.status) && (
                <Button
                  onClick={handleSendForApproval}
                  variant="contained"
                  color="success"
                  size="large">
                  Send For Approval
                </Button>
              )}
              {MigrationStatus.InApproval === migration.status && (
                <>
                  {migration.isOwner && (
                    <Typography variant="subtitle2" color="error">
                      Approval cannot be done by the person who migrated the
                      case
                    </Typography>
                  )}
                  {!migration.isOwner && (
                    <Button
                      onClick={handleApprove}
                      variant="contained"
                      color="warning"
                      disabled={
                        items.filter((i) => i.approval).length !== items.length
                      }
                      size="large">
                      Approve
                    </Button>
                  )}
                </>
              )}
              <Button
                onClick={handlePreview}
                variant="outlined"
                color="primary"
                size="large">
                Preview
              </Button>
              {migration.status !== MigrationStatus.Complete && (
                <Button
                  onClick={handleGlobalInventory}
                  variant="outlined"
                  color="primary"
                  size="large">
                  Global Inventory
                </Button>
              )}
              {MigrationStatusesBeingMigrated.includes(migration.status) && (
                <Button
                  onClick={handleStatus}
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowDropDownIcon />}
                  ref={statusRef}>
                  {migration.status === MigrationStatus.InProgress
                    ? 'In Progress'
                    : migration.status}
                </Button>
              )}
              <IconButton
                aria-label="delete"
                color="primary"
                onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Paper
              elevation={8}
              sx={{
                flex: '0 0 70%',
                display: 'flex',
                justifyContent: 'space-between',
                marginRight: '10px',
              }}>
              <PDFPreviewSection
                pdfURL={migration.fileURL}
                suggestions={migration.suggestions}
                highlightItemId={highlightItemId}
              />
            </Paper>
            {migration != null && (
              <Paper
                elevation={8}
                sx={(theme) => ({
                  width: '462px',
                  height: `calc(100vh - ${theme.spacing(20)})`,
                  overflow: 'auto',
                })}>
                <Information
                  items={items}
                  suggestions={migration.suggestions}
                  migration={migration}
                  deleteItem={handleDeleteItem}
                  itemUpdated={handleItemUpdated}
                  noteUpdated={handleNoteUpdated}
                  noteDeleted={handleNoteDeleted}
                  procedureUpdated={handleProcedureUpdated}
                  highlightItem={handleHighlightItem}
                  suggestionUpdate={handleSuggestionUpdate}
                  handleItemApprovalUpdate={handleItemApprovalUpdate}
                  refreshSuggestions={handleRefreshSuggestions}
                  inventoryItemAdded={handleInventoryItemAdded}
                />
              </Paper>
            )}
          </Box>
          <MigrationStatusSelection
            open={statusOpen}
            close={handleStatusClosed}
            anchorEl={statusRef}
            migrationId={migration.id}
            currentStatus={migration.status}
          />
        </Layout>
      )}
      <InventoryDialog
        open={inventoryOpen}
        close={() => setInventoryOpen(false)}
      />
      <PreviewDialog
        open={previewOpen}
        close={() => setPreviewOpen(false)}
        items={items}
      />
      <ConfirmationDialog
        title="Finish Migration"
        information="Are you sure you have finished migrating this card and want to send it for approval?"
        open={confirmOpen}
        buttons={['Cancel', 'Confirm']}
        confirmationClicked={handleConfirmClicked}
      />
    </>
  );
}

export default MigrationDetails;
